// src/components/Hermes/Tasks/types.ts
// ============================================
// Hermes 任务面板类型定义 + 工具函数
//
// 包含：
//   - TaskStatus / TaskDisplayInfo 展示类型
//   - 任务 ID → 图标 / 中文名 / 描述 映射表
//   - toTaskDisplayInfo：由 ScheduledTask + History 派生展示信息
//   - cronToHuman / formatDuration / formatTime / cronToHour 工具函数
// ============================================

import type {
  ScheduledTask,
  TaskExecutionRecord,
} from "../../../runtime/SchedulerRuntime/types";

// ============================================
// 展示类型
// ============================================

/** 任务展示状态 */
export type TaskStatus = "idle" | "running" | "success" | "failed" | "paused";

/** 任务展示信息（由 ScheduledTask + History 派生） */
export interface TaskDisplayInfo {
  id: string;
  name: string;
  cron?: string;
  description?: string;
  status: TaskStatus;
  lastRunAt?: string;
  nextRunAt?: string;
  runCount: number;
  successCount: number;
  failCount: number;
  /** 上次执行耗时（毫秒） */
  lastDuration?: number;
  /** 上次错误信息 */
  lastError?: string;
  /** 是否启用 */
  enabled: boolean;
}

// ============================================
// 任务元数据映射表
// ============================================

/** 任务 ID → 图标 emoji */
export const TASK_ICONS: Record<string, string> = {
  "builtin.hermes-rhythm-ainews": "📰",
  "builtin.hermes-rhythm-content": "✍️",
  "builtin.hermes-rhythm-techscan": "🔍",
  "builtin.hermes-rhythm-crmsync": "👤",
  "builtin.hermes-rhythm-review": "📝",
  "builtin.hermes-rhythm-consolidate": "🧠",
};

/** 任务 ID → 中文名 */
export const TASK_CN_NAMES: Record<string, string> = {
  "builtin.hermes-rhythm-ainews": "AI 新闻采集",
  "builtin.hermes-rhythm-content": "内容生成",
  "builtin.hermes-rhythm-techscan": "技术扫描",
  "builtin.hermes-rhythm-crmsync": "CRM 同步",
  "builtin.hermes-rhythm-review": "每日复盘",
  "builtin.hermes-rhythm-consolidate": "记忆整合",
};

/** 任务 ID → 中文描述 */
export const TASK_DESCRIPTIONS: Record<string, string> = {
  "builtin.hermes-rhythm-ainews": "采集 AI 行业新闻，沉淀为每日 Idea",
  "builtin.hermes-rhythm-content": "基于知识库生成多平台内容",
  "builtin.hermes-rhythm-techscan": "扫描 GitHub / OpenAI / Claude 等技术动态",
  "builtin.hermes-rhythm-crmsync": "多渠道客户线索自动归档",
  "builtin.hermes-rhythm-review": "AI 生成每日复盘与明日建议",
  "builtin.hermes-rhythm-consolidate": "Idea → Card → Domain 记忆升级",
};

// ============================================
// 派生函数
// ============================================

/**
 * 由 ScheduledTask + History 计算 TaskDisplayInfo
 *
 * 状态判定优先级：
 *   1. triggering === true → running（本地乐观状态）
 *   2. !task.enabled → paused
 *   3. 最近一次历史 success → success / failed
 *   4. 无历史 → idle
 */
export function toTaskDisplayInfo(
  task: ScheduledTask,
  history: TaskExecutionRecord[],
  triggering: boolean
): TaskDisplayInfo {
  // 该任务的历史记录（history 按时间倒序）
  const taskHistory = history.filter((h) => h.taskId === task.id);
  const successCount = taskHistory.filter((h) => h.success).length;
  const failCount = taskHistory.filter((h) => !h.success).length;
  const latest = taskHistory[0];

  // 计算状态
  let status: TaskStatus = "idle";
  if (triggering) {
    status = "running";
  } else if (!task.enabled) {
    status = "paused";
  } else if (latest) {
    status = latest.success ? "success" : "failed";
  }

  return {
    id: task.id,
    name: TASK_CN_NAMES[task.id] || task.name,
    cron: task.cron,
    description: TASK_DESCRIPTIONS[task.id],
    status,
    lastRunAt: task.lastRunAt || latest?.startedAt,
    nextRunAt: task.nextRunAt,
    runCount: task.runCount,
    successCount,
    failCount,
    lastDuration: latest?.duration,
    lastError: latest?.error,
    enabled: task.enabled,
  };
}

// ============================================
// 格式化工具函数
// ============================================

/**
 * 将 cron 表达式转为人类可读
 * 仅处理每日定时场景（如 "0 9 * * *" → "每天 09:00"）
 */
export function cronToHuman(cron?: string): string {
  if (!cron) return "未设置";
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [minute, hour, day, month, weekday] = parts;
  const hh = hour === "*" ? "00" : hour.padStart(2, "0");
  const mm = minute === "*" ? "00" : minute.padStart(2, "0");
  // 每天定时
  if (day === "*" && month === "*" && weekday === "*") {
    return `每天 ${hh}:${mm}`;
  }
  return cron;
}

/** 格式化耗时（ms → 1.2s / 3m5s） */
export function formatDuration(ms?: number): string {
  if (ms === undefined || ms === null) return "-";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m${s}s`;
}

/** 格式化时间（HH:mm） */
export function formatTime(iso?: string): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleTimeString("zh-CN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "-";
  }
}

/** 格式化日期时间（MM-dd HH:mm） */
export function formatDateTime(iso?: string): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("zh-CN", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "-";
  }
}

/**
 * 从 cron 中提取小时（用于时间线定位）
 * 返回 0-24 的小数（如 09:30 → 9.5），无法解析返回 null
 */
export function cronToHour(cron?: string): number | null {
  if (!cron) return null;
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const minute = parseInt(parts[0], 10);
  const hour = parseInt(parts[1], 10);
  if (isNaN(minute) || isNaN(hour)) return null;
  return hour + minute / 60;
}

// 透传底层类型，便于组件统一引用
export type { ScheduledTask, TaskExecutionRecord };
